import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Checkbox, Space, Table, Typography } from "antd";
import { Button, Drawer } from "../../components/Common";
import { useMutations } from "../../hooks/useApiMutation";

const { Text } = Typography;

const ACTIONS = [
  { key: "canView", label: "View" },
  { key: "canCreate", label: "Create" },
  { key: "canUpdate", label: "Update" },
  { key: "canDelete", label: "Delete" },
];

const EMPTY_ROW = {
  canView: false,
  canCreate: false,
  canUpdate: false,
  canDelete: false,
};

/**
 * The developer permission matrix: one row per module, one checkbox per action.
 *
 * Saving replaces the whole matrix — the API drops rows that grant nothing, so an
 * all-unchecked module simply disappears rather than lingering as an empty grant.
 */
const PermissionsDrawer = ({ user, onClose, onSaved }) => {
  const { mutate, loading } = useMutations();
  const mutateRef = useRef(mutate);
  mutateRef.current = mutate;

  const [modules, setModules] = useState([]);
  const [matrix, setMatrix] = useState({});
  const [loadingData, setLoadingData] = useState(false);

  const load = useCallback(async (userId) => {
    setLoadingData(true);

    await mutateRef.current("usersCatalog", {
      method: "get",
      endpoint: "/users/catalog",
      onSuccess: (data) => setModules(data?.modules || []),
      onError: () => setModules([]),
    });

    await mutateRef.current("userDetail", {
      method: "get",
      endpoint: `/users/${userId}`,
      onSuccess: (data) => {
        const next = {};
        (data?.permissions || []).forEach((p) => {
          next[p.module] = {
            canView: Boolean(p.canView),
            canCreate: Boolean(p.canCreate),
            canUpdate: Boolean(p.canUpdate),
            canDelete: Boolean(p.canDelete),
          };
        });
        setMatrix(next);
      },
      onError: () => setMatrix({}),
    });

    setLoadingData(false);
  }, []);

  useEffect(() => {
    if (user?.id) load(user.id);
  }, [user?.id, load]);

  const toggle = (module, action, checked) => {
    setMatrix((prev) => {
      const row = { ...EMPTY_ROW, ...(prev[module] || {}), [action]: checked };
      // A write grant is meaningless without read access, so keep View in step
      // with the server-side rule that write implies read.
      if (checked && action !== "canView") row.canView = true;
      return { ...prev, [module]: row };
    });
  };

  const toggleWholeModule = (module, checked) => {
    setMatrix((prev) => ({
      ...prev,
      [module]: {
        canView: checked,
        canCreate: checked,
        canUpdate: checked,
        canDelete: checked,
      },
    }));
  };

  const rows = useMemo(
    () => modules.map((module) => ({ module, ...EMPTY_ROW, ...(matrix[module] || {}) })),
    [modules, matrix]
  );

  const grantedCount = rows.filter(
    (r) => r.canView || r.canCreate || r.canUpdate || r.canDelete
  ).length;

  const columns = [
    {
      title: "Module",
      dataIndex: "module",
      key: "module",
      render: (module, row) => (
        <Space direction="vertical" size={0}>
          <Text strong>{module}</Text>
          <Button
            variant="ghost"
            style={{ padding: 0, height: "auto", fontSize: 11 }}
            onClick={() =>
              toggleWholeModule(
                module,
                !(row.canView && row.canCreate && row.canUpdate && row.canDelete)
              )
            }
          >
            toggle all
          </Button>
        </Space>
      ),
    },
    ...ACTIONS.map((action) => ({
      title: action.label,
      key: action.key,
      align: "center",
      render: (_, row) => (
        <Checkbox
          checked={row[action.key]}
          onChange={(e) => toggle(row.module, action.key, e.target.checked)}
        />
      ),
    })),
  ];

  const save = async () => {
    const permissions = rows
      .filter((r) => r.canView || r.canCreate || r.canUpdate || r.canDelete)
      .map(({ module, canView, canCreate, canUpdate, canDelete }) => ({
        module,
        canView,
        canCreate,
        canUpdate,
        canDelete,
      }));

    await mutate("saveUserPermissions", {
      method: "put",
      endpoint: `/users/${user.id}/permissions`,
      payload: { permissions },
      onSuccess: () => {
        onSaved?.();
        onClose();
      },
    });
  };

  return (
    <Drawer
      open={Boolean(user)}
      onClose={onClose}
      size="lg"
      title={user ? `Module access — ${user.username}` : "Module access"}
      loading={loadingData}
      footer={
        <Space style={{ width: "100%", justifyContent: "flex-end" }}>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={loading?.saveUserPermissions} onClick={save}>
            Save access
          </Button>
        </Space>
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={`${grantedCount} of ${rows.length} modules granted`}
        description="A module with no boxes ticked is invisible to this developer, and the API refuses its endpoints."
      />
      <Table
        rowKey="module"
        columns={columns}
        dataSource={rows}
        pagination={false}
        size="small"
      />
    </Drawer>
  );
};

export default PermissionsDrawer;
